import React, { useCallback, type FC } from 'react';
import type { ResponseCluster } from '@mentimeter/http-clients';
import { getGroupsShapes } from '@mentimeter/slides-presentation-ui/groups/group-shapes';
import type { QuestionWithSlide } from '@mentimeter/voting-schema/api-types-overrides';
import { MentiError } from '@mentimeter/error-utils/error';
import { UNGROUPED_CLUSTER_ID } from '../../ungroupedClusterId';
import type { TextResponses } from '../types';
import { useVisualizationColors } from '../../../useVisualizationColors';
import { useTranslate } from '../../../../features/localization/translate';
import { useGroupedResults } from '../../hooks/useGroupedResults';
import { useResults } from '../../hooks/useResults';
import { useSubmitUpvote } from '../../hooks/useSubmitUpvote';
import { useSubmitAllUpvotes } from '../../hooks/useSubmitAllUpvotes';
import { tryGetFirstInteractiveContentChoiceId } from '../../../../utils/getInteractiveContentChoiceId';
import type { Upvotes, useUpvotes } from './useUpvotes';
import { UpvotingView, type Answers } from './UpvotingView';
interface UpvotingViewDataHandlerProps {
  question: QuestionWithSlide;
  isGrouped: boolean;
  upvoting: ReturnType<typeof useUpvotes>;
}

export const UpvotingViewDataHandler: FC<UpvotingViewDataHandlerProps> = ({
  isGrouped,
  upvoting,
  question,
}) => {
  const {
    public_key: publicKey,
    max_votes: maxVotes,
    multiple_votes_on_answers: multipleUpvotesEnabled,
  } = question;

  const legacySubmitUpvote = useSubmitUpvote(publicKey);
  const interactiveContentChoiceId =
    tryGetFirstInteractiveContentChoiceId(question);
  const migratedSubmitUpvotes = useSubmitAllUpvotes(
    interactiveContentChoiceId ?? '',
  );

  const submitUpvotes = useCallback(
    async (upvotes: Upvotes) => {
      if (question.isMigrated) {
        if (!interactiveContentChoiceId) {
          throw new MentiError('Expected interactive content choice id', {
            feature: 'slides',
            tags: {
              migrated: true,
            },
          });
        }
        await migratedSubmitUpvotes(upvotes);
      } else {
        const promises = Object.entries(upvotes).flatMap(([voteId, count]) => {
          return [...Array(count).fill(0)].map(() =>
            legacySubmitUpvote(voteId),
          );
        });
        await Promise.all(promises);
      }
    },
    [
      question.isMigrated,
      interactiveContentChoiceId,
      migratedSubmitUpvotes,
      legacySubmitUpvote,
    ],
  );

  const { fillColors } = useVisualizationColors();
  const translate = useTranslate();

  const { responses = [] } = useResults<TextResponses>({
    isMigrated: Boolean(question.isMigrated),
    questionPublicKey: publicKey,
    interactiveContentChoiceId,
    withUpvotes: false,
    shouldFetch: !isGrouped,
  }).data;

  const groupedResults = useGroupedResults({
    questionPublicKey: publicKey,
    shouldFetch: isGrouped,
  }).data;

  const ungroupedResponsesAsGroup: ResponseCluster = {
    id: UNGROUPED_CLUSTER_ID,
    label: translate('open_ended.new_responses'),
    responses: groupedResults?.unclustered_responses ?? [],
    clustering_id: UNGROUPED_CLUSTER_ID,
    total_upvotes: 0,
  };
  const isUngroupedResponsesEmpty =
    ungroupedResponsesAsGroup.responses.length === 0;

  const groupedResponses = groupedResults?.clusters ?? [];
  const groups = isUngroupedResponsesEmpty
    ? groupedResponses
    : [...groupedResponses, ungroupedResponsesAsGroup];

  const shapes = getGroupsShapes(groups, fillColors, UNGROUPED_CLUSTER_ID);
  const groupsWithShapes = groups.map((group) => ({
    ...group,
    shape: shapes.get(group.id),
  }));

  const answers: Answers = isGrouped
    ? {
        type: 'groups',
        groups: groupsWithShapes,
      }
    : {
        type: 'responses',
        responses,
      };

  return (
    <UpvotingView
      multipleUpvotesEnabled={multipleUpvotesEnabled}
      maxVotes={maxVotes}
      responses={answers}
      submitUpvotes={submitUpvotes}
      upvoting={upvoting}
    />
  );
};
